/**
 * London Smoke & Cure: optional `lists/{listId}/contacts/{contactId}` storage when
 * `metadata.contactsInSubcollection` is true. Migrating copies embedded `recipients[]` into
 * contact docs then removes the `recipients` field on the list document (`deleteField()`).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  writeBatch,
  updateDoc,
  deleteDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import {
  LISTS_COLL,
  buildRecipientsToSaveForListDoc,
  assertNoAccidentalRecipientRemoval,
  uniqueId,
  toLower,
} from './contactManagerUtils';
import {
  LIST_CONTACTS_SUBCOL,
  LIST_METADATA_CONTACTS_SUBCOLLECTION,
  isListContactsSubcollectionMode,
} from './listContactsConstants';

/** Stay under Firestore 500 writes per batch (headroom for list metadata update). */
const BATCH_MAX_OPS = 450;

export { LIST_CONTACTS_SUBCOL, LIST_METADATA_CONTACTS_SUBCOLLECTION, isListContactsSubcollectionMode };

/**
 * Parent `lists/{id}` snapshots often carry an empty or missing `recipients` after migration to
 * `contacts/`; merge with the previous in-memory list so UI rows are not wiped on unrelated parent
 * updates (pipeline, timestamps) before or after the `contacts/` listener runs.
 *
 * @param {object} metadata `lists/{id}.metadata`
 * @param {unknown} embeddedRecipients `lists/{id}.recipients` from the same snapshot
 * @param {{ recipients?: object[] }|null|undefined} previousList prior React state for this list id
 * @returns {object[]}
 */
export function recipientsForListSnapshotMerge(metadata, embeddedRecipients, previousList) {
  const embedded = Array.isArray(embeddedRecipients) ? embeddedRecipients : [];
  if (!isListContactsSubcollectionMode(metadata)) {
    return embedded;
  }
  if (embedded.length > 0) return embedded;
  const prev = previousList && Array.isArray(previousList.recipients) ? previousList.recipients : [];
  return prev.length ? prev : [];
}

/**
 * Latest CRM rows for a list: either embedded `recipients` or all `contacts/*` docs.
 * @param {string} listId
 * @param {object} listData `lists/{listId}` document data
 * @returns {Promise<object[]>}
 */
export async function fetchServerRecipientsRowsForList(listId, listData) {
  const data = listData && typeof listData === 'object' ? listData : {};
  if (isListContactsSubcollectionMode(data.metadata)) {
    const col = collection(db, LISTS_COLL, listId, LIST_CONTACTS_SUBCOL);
    const snap = await getDocs(col);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  return Array.isArray(data.recipients) ? data.recipients : [];
}

export function ensureRecipientFirestoreDocId(row) {
  if (!row || typeof row !== 'object') return null;
  if (row.id && String(row.id).trim()) return String(row.id).trim();
  const em = toLower(row.email || row.Email || '');
  return em ? `${em}-${uniqueId()}` : `row-${uniqueId()}`;
}

/**
 * Build the normalised contact rows that migrate would write (no Firestore writes).
 * @param {object} listData `lists/{listId}` document data from Firestore
 * @returns {{ alreadyMigrated: true, contactCount: number } | { alreadyMigrated: false, foldedAll: object[], embeddedCount: number }}
 */
export function computeMigrateListToContactsPayload(listData) {
  const data = listData && typeof listData === 'object' ? listData : {};
  const meta = data.metadata && typeof data.metadata === 'object' ? data.metadata : {};
  if (meta[LIST_METADATA_CONTACTS_SUBCOLLECTION] === true) {
    const n = typeof meta.contactCount === 'number' ? meta.contactCount : 0;
    return { alreadyMigrated: true, contactCount: n };
  }
  const embedded = Array.isArray(data.recipients) ? data.recipients : [];
  const embeddedCount = embedded.length;
  const withIds = embedded.map((r) => ({
    ...r,
    id: ensureRecipientFirestoreDocId(r),
  }));
  for (const r of withIds) {
    if (!r.id) {
      throw new Error(
        'London Smoke & Cure: migration stopped — a row has no id and no email. Fix that row in Firebase Console, then run migrate again.',
      );
    }
  }
  const foldedAll = buildRecipientsToSaveForListDoc(data, [], withIds);
  for (const row of foldedAll) {
    if (!row.id) {
      throw new Error('London Smoke & Cure: migration stopped — normalised row missing id.');
    }
  }
  return { alreadyMigrated: false, foldedAll, embeddedCount };
}

/**
 * Read-only preview for Contact Manager before migrating embedded recipients to contacts/.
 * @param {string} listId
 * @returns {Promise<object>}
 */
export async function getMigrateListToContactsSubcollectionPreview(listId) {
  const listRef = doc(db, LISTS_COLL, listId);
  const listSnap = await getDoc(listRef);
  if (!listSnap.exists()) {
    return { ok: false, error: 'London Smoke & Cure: list was not found.' };
  }
  const listData = listSnap.data();
  try {
    const payload = computeMigrateListToContactsPayload(listData);
    if (payload.alreadyMigrated) {
      return {
        ok: true,
        alreadyMigrated: true,
        listId,
        listName: listData.name || listId,
        contactCount: payload.contactCount,
      };
    }
    const { foldedAll, embeddedCount } = payload;
    const previewLimit = 50;
    const previewRows = foldedAll.slice(0, previewLimit).map((row) => ({
      docId: row.id,
      email: row.email || row.Email || '',
      leadStatus: row.leadStatus || '',
      company: row.company || row['Company Name'] || '',
    }));
    return {
      ok: true,
      alreadyMigrated: false,
      listId,
      listName: listData.name || listId,
      embeddedCount,
      contactDocsCount: foldedAll.length,
      mergedAwayCount: Math.max(0, embeddedCount - foldedAll.length),
      previewRows,
      previewTruncated: foldedAll.length > previewLimit,
    };
  } catch (e) {
    return { ok: false, error: e?.message || 'Preview failed.' };
  }
}

async function commitBatches(operations) {
  let batch = writeBatch(db);
  let ops = 0;
  for (const fn of operations) {
    if (ops >= BATCH_MAX_OPS) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
    fn(batch);
    ops += 1;
  }
  if (ops > 0) {
    await batch.commit();
  }
}

/**
 * Save intended recipients for a list that uses per-contact documents (not `recipients[]`).
 *
 * @param {string} listId
 * @param {object[]} intendedRecipients
 * @param {{ bypassRemovalGuard?: boolean, serverBaselineMerge?: boolean }} [options]
 */
export async function saveListContactsSubcollection(listId, intendedRecipients, options = {}) {
  const clientRows = Array.isArray(intendedRecipients) ? intendedRecipients : [];
  const listRef = doc(db, LISTS_COLL, listId);
  const listSnap = await getDoc(listRef);
  if (!listSnap.exists()) {
    throw new Error(
      'London Smoke & Cure: this list no longer exists. Refresh Contact Manager or Newsletter Editor, then try again.',
    );
  }
  const listData = listSnap.data();
  if (!isListContactsSubcollectionMode(listData.metadata)) {
    throw new Error(
      'London Smoke & Cure: internal error — saveListContactsSubcollection called for a list that is not in contacts subcollection mode.',
    );
  }

  const { bypassRemovalGuard, serverBaselineMerge = true } = options;

  const serverRows = await fetchServerRecipientsRowsForList(listId, listData);
  const recipientsToSave = serverBaselineMerge
    ? buildRecipientsToSaveForListDoc(listData, serverRows, clientRows)
    : buildRecipientsToSaveForListDoc(listData, [], clientRows);

  if (serverRows.length) {
    assertNoAccidentalRecipientRemoval(serverRows, recipientsToSave, { bypassRemovalGuard });
  }

  const normalized = [];
  for (const r of recipientsToSave) {
    const docId = ensureRecipientFirestoreDocId(r);
    if (!docId) {
      throw new Error(
        'London Smoke & Cure: a contact row is missing both id and a usable email, so it cannot be stored. Fix or remove that row, then try again.',
      );
    }
    normalized.push({ docId, data: { ...r, id: docId } });
  }

  const nextIds = new Set(normalized.map((x) => x.docId));
  const staleIds = serverRows.map((r) => r && r.id).filter(Boolean).filter((id) => !nextIds.has(id));

  const opFns = [];
  for (const sid of staleIds) {
    opFns.push((b) => b.delete(doc(db, LISTS_COLL, listId, LIST_CONTACTS_SUBCOL, sid)));
  }
  for (const { docId, data } of normalized) {
    opFns.push((b) => b.set(doc(db, LISTS_COLL, listId, LIST_CONTACTS_SUBCOL, docId), data));
  }
  await commitBatches(opFns);

  await updateDoc(listRef, {
    updatedAt: serverTimestamp(),
    'metadata.contactCount': normalized.length,
    'metadata.lastUpdated': serverTimestamp(),
  });
}

/**
 * Copy embedded `recipients[]` into `contacts/` and set `metadata.contactsInSubcollection` + clear array.
 *
 * @param {string} listId
 * @param {(p: { message: string, fraction: number }) => void} [onProgress]
 * @returns {Promise<{ alreadyMigrated: boolean, contactCount: number }>}
 */
export async function migrateListRecipientsToContactsSubcollection(listId, onProgress) {
  const listRef = doc(db, LISTS_COLL, listId);
  const listSnap = await getDoc(listRef);
  if (!listSnap.exists()) {
    throw new Error('London Smoke & Cure: list was not found.');
  }
  const listData = listSnap.data();
  const notify = (message, fraction) => {
    if (typeof onProgress === 'function') onProgress({ message, fraction });
  };

  notify('Preparing rows…', 0);

  let foldedAll;
  try {
    const payload = computeMigrateListToContactsPayload(listData);
    if (payload.alreadyMigrated) {
      const n = typeof payload.contactCount === 'number' ? payload.contactCount : 0;
      return { alreadyMigrated: true, contactCount: n };
    }
    foldedAll = payload.foldedAll;
  } catch (e) {
    throw e instanceof Error ? e : new Error(String(e?.message || e));
  }

  const total = foldedAll.length;
  let done = 0;

  const opFns = [];
  for (const row of foldedAll) {
    const docId = row.id;
    if (!docId) {
      throw new Error('London Smoke & Cure: migration stopped — normalised row missing id.');
    }
    opFns.push((b) => b.set(doc(db, LISTS_COLL, listId, LIST_CONTACTS_SUBCOL, docId), { ...row, id: docId }));
  }

  let idx = 0;
  while (idx < opFns.length) {
    const batch = writeBatch(db);
    let n = 0;
    while (idx < opFns.length && n < BATCH_MAX_OPS) {
      opFns[idx](batch);
      idx += 1;
      n += 1;
    }
    await batch.commit();
    done = idx;
    notify(`Writing contacts (${Math.min(done, total)} of ${total})…`, total ? done / total : 1);
  }

  await updateDoc(listRef, {
    recipients: deleteField(),
    updatedAt: serverTimestamp(),
    [`metadata.${LIST_METADATA_CONTACTS_SUBCOLLECTION}`]: true,
    'metadata.contactCount': foldedAll.length,
    'metadata.lastUpdated': serverTimestamp(),
    'metadata.migratedToContactsSubcollectionAt': serverTimestamp(),
  });

  notify('Migration finished.', 1);
  return { alreadyMigrated: false, contactCount: foldedAll.length };
}

/**
 * Delete `lists/{listId}` and, when migrated, all `contacts/*` docs first (Firestore does not cascade).
 * @param {string} listId
 */
export async function deleteListDocumentAndNestedContacts(listId) {
  const listRef = doc(db, LISTS_COLL, listId);
  const listSnap = await getDoc(listRef);
  if (!listSnap.exists()) return;
  const data = listSnap.data() || {};
  if (isListContactsSubcollectionMode(data.metadata)) {
    const col = collection(db, LISTS_COLL, listId, LIST_CONTACTS_SUBCOL);
    const snap = await getDocs(col);
    const ids = snap.docs.map((d) => d.id);
    let idx = 0;
    while (idx < ids.length) {
      const batch = writeBatch(db);
      let n = 0;
      while (idx < ids.length && n < BATCH_MAX_OPS) {
        batch.delete(doc(db, LISTS_COLL, listId, LIST_CONTACTS_SUBCOL, ids[idx]));
        idx += 1;
        n += 1;
      }
      await batch.commit();
    }
  }

  await deleteDoc(listRef);
}
